import { createTheme } from "@mui/material/styles";

/**
 * MUI 전역 테마 — palette·typography·컴포넌트 styleOverrides.
 * designSystem.js 의 토큰을 받아 앱 전체 MUI 기본값을 만든다.
 * 컴포넌트별 sx 프리셋은 ./presets/ 에 있다 — 여기는 "MUI 기본값", 프리셋은 "화면에서 골라 쓰는 조합".
 */
export const createMuiTheme = (tokens) => {
    /**
     * 공통 드롭다운 스펙 — 시스템의 "단일 드롭다운 디자인" 원본.
     * Select 메뉴(MuiMenu/MuiMenuItem)와 Autocomplete/ComboBox 팝업(MuiAutocomplete)이
     * 완전히 같은 종이(paper)·항목(item)·선택색으로 보이도록 여기 한 곳에서만 정의한다.
     * (개별 컴포넌트/화면에서 slotProps·sx 로 드롭다운을 꾸미지 말 것 — 여기 값을 바꾼다.)
     */
    // 드롭다운 팝업(종이) — 카드 같은 테두리 + 떠 있는 그림자 + 트리거와 4px 간격.
    const dropdownPaper = {
        borderRadius: 8,
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
        backgroundImage: "none",
        marginTop: 4,
    };
    // 드롭다운 항목 기본 — 글자 14px, hover 시 primaryBg.
    // whiteSpace/wordBreak: 항목이 길어도 폼필드 너비 안에서 줄바꿈되어 가로로 넘치지 않게 한다.
    const dropdownItem = {
        fontSize: 14,
        borderRadius: 6,
        margin: "1px 6px",
        minHeight: "auto",
        paddingTop: 7,
        paddingBottom: 7,
        color: tokens.colors.textPrimary,
        whiteSpace: "normal",
        wordBreak: "break-word",
        "&:hover": { backgroundColor: tokens.colors.primaryBg },
    };
    // 선택된 항목 — 연한 파랑 배경 + primary 글자(사진의 강조 상태).
    const dropdownItemSelected = {
        backgroundColor: tokens.colors.primaryLight,
        color: tokens.colors.primary,
        fontWeight: 600,
    };

    return createTheme({
    /**
     * MUI palette
     * - MUI 컴포넌트의 color="primary", color="error" 등이 참조하는 전역 색상이다.
     * - 여기 값을 바꾸면 Button, Checkbox, Tabs, CircularProgress 등 MUI 기본 색상이 함께 바뀐다.
     */
    palette: {
        primary: {
            main: tokens.colors.primary,
            dark: tokens.colors.primaryDark,
            light: tokens.colors.primarySoft,
            contrastText: "#ffffff",
        },
        secondary: { main: tokens.colors.secondary },
        success: { main: tokens.colors.success },
        error: { main: tokens.colors.error },
        warning: { main: tokens.colors.warning },
        info: { main: tokens.colors.info },
        background: {
            default: tokens.colors.bg,
            paper: tokens.colors.surface,
        },
        text: {
            primary: tokens.colors.textPrimary,
            secondary: tokens.colors.textSecondary,
            disabled: tokens.colors.textMuted,
        },
        divider: tokens.colors.border,
    },

    /**
     * MUI typography
     * - Typography variant="h1" 같은 MUI variant의 기본값이다.
     * - 현재 프로젝트는 sx를 많이 쓰므로, 화면 공통 텍스트는 designSystem.js의 ds.typography를 우선 사용한다.
     */
    typography: {
        fontFamily: [
            "Pretendard",
            "-apple-system",
            "BlinkMacSystemFont",
            "'Segoe UI'",
            "Roboto",
            "sans-serif",
        ].join(","),
        fontSize: 14,
        h1: { fontWeight: 800, letterSpacing: "-0.5px" },
        h2: { fontWeight: 800, letterSpacing: "-0.4px" },
        h3: { fontWeight: 700, letterSpacing: "-0.3px" },
        h4: { fontWeight: 700 },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
        subtitle1: { fontWeight: 600 },
        subtitle2: { fontWeight: 600 },
    },

    /**
     * MUI shape
     * - MUI 컴포넌트 기본 radius 기준값이다.
     * - sx에서 borderRadius: 1을 쓰면 대체로 이 값 8px 기준으로 계산된다.
     * - 값을 키우면 Button/Paper/Input 등 MUI 기본 컴포넌트가 전반적으로 둥글어진다.
     */
    shape: { borderRadius: 8 },

    /**
     * MUI shadow scale
     * - elevation={1}, elevation={2} 같은 Paper 그림자 단계다.
     * - 현재는 elevation을 거의 쓰지 않고 border 기반 UI를 쓰므로 낮은 단계만 정의하고 나머지는 none 처리한다.
     */
    shadows: [
        "none",
        "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
        "0 2px 8px rgba(0,0,0,0.07)",
        "0 4px 16px rgba(0,0,0,0.08)",
        "0 8px 24px rgba(0,0,0,0.09)",
        ...Array(20).fill("none"),
    ],

    /**
     * MUI components styleOverrides
     * - 앱 전체 MUI 컴포넌트의 기본 스타일이다.
     * - 개별 화면에서 sx를 안 줘도 이 설정이 기본 적용된다.
     * - 특정 화면에서만 다르게 보여야 하면 해당 화면 sx가 이 값을 덮어쓴다.
     */
    components: {
        // CssBaseline 전역 CSS. body/input/button까지 Pretendard를 우선 적용한다.
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                },
                body: {
                    fontFamily: [
                        "Pretendard",
                        "-apple-system",
                        "BlinkMacSystemFont",
                        "'Segoe UI'",
                        "Roboto",
                        "sans-serif",
                    ].join(","),
                    letterSpacing: "-0.2px",
                },
                "button, input, textarea, select": {
                    fontFamily: "inherit",
                },
                "*, *::before, *::after": {
                    fontVariantNumeric: "tabular-nums lining-nums",
                    fontFeatureSettings: '"tnum" 1, "lnum" 1',
                },
            },
        },

        // Button 전역 기본값. 기본 높이, radius, hover shadow 제거를 관리한다.
        MuiButton: {
            defaultProps: {
                size: "medium",
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 6,
                    boxShadow: "none",
                    minHeight: 36,
                    "&:hover": { boxShadow: "none" },
                },
                sizeSmall: { minHeight: 32, fontSize: 12 },
                sizeMedium: { minHeight: 36, fontSize: 13 },
                sizeLarge: { minHeight: 42, fontSize: 14 },
                containedPrimary: {
                    backgroundColor: tokens.colors.primary,
                    "&:hover": { backgroundColor: tokens.colors.primaryDark },
                },
                outlinedPrimary: {
                    borderColor: tokens.colors.primaryBorder,
                    color: tokens.colors.primary,
                    "&:hover": {
                        backgroundColor: tokens.colors.primaryBg,
                        borderColor: tokens.colors.primary,
                    },
                },
            },
        },

        // TextField 전역 기본값. variant/size/fullWidth를 지정하지 않아도 같은 입력 컴포넌트로 보이게 한다.
        MuiTextField: {
            defaultProps: {
                variant: "outlined",
                size: "small",
                fullWidth: true,
            },
        },

        // Paper 전역 기본값. 카드/패널을 border 기반으로 보이게 한다.
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    border: `1px solid ${tokens.colors.border}`,
                    borderRadius: 8,
                    backgroundImage: "none",
                },
            },
        },

        // TextField variant="outlined"의 실제 input box 기본값이다.
        // placeholder 글자 크기는 input의 "&::placeholder" fontSize에서 조절한다.
        // InputLabel이 잘리는 경우는 보통 모달/컨테이너 상단 padding이 부족해서 발생한다.
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.colors.primarySoft },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: tokens.colors.primary,
                        borderWidth: 1.5,
                    },
                },
                input: {
                    fontFamily: "inherit",
                    fontSize: 14,
                    "&::placeholder": {
                        fontSize: 13,
                        color: tokens.colors.textMuted,
                        opacity: 1,
                    },
                },
                notchedOutline: { borderColor: tokens.colors.border },
            },
        },
        // TextField label 포커스 색상. 입력 중일 때 primary 색으로 바뀐다.
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: "inherit",
                    fontSize: 14,
                    color: tokens.colors.textSecondary,
                    "&.Mui-focused": { color: tokens.colors.primary },
                },
                shrink: {
                    fontSize: 13,
                },
            },
        },

        // Tabs 전역 기본값. 선택 indicator 색/두께를 관리한다.
        MuiTabs: {
            styleOverrides: {
                root: { minHeight: 40 },
                indicator: {
                    backgroundColor: tokens.colors.primary,
                    height: 2.5,
                    borderRadius: "2px 2px 0 0",
                },
            },
        },
        // Tab 전역 기본값. 선택된 탭은 primary 색으로 바뀐다.
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    minHeight: 40,
                    color: tokens.colors.secondary,
                    "&.Mui-selected": { color: tokens.colors.primary },
                },
            },
        },

        // Chip 전역 기본값. 별도 sx가 없을 때 기본 radius와 굵기를 통일한다.
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: 6, fontWeight: 600 },
            },
        },

        // Dialog 전역 기본값. 모달의 기본 radius를 통일한다.
        MuiDialog: {
            styleOverrides: {
                paper: { borderRadius: 12, border: "none" },
            },
        },

        // Divider 전역 색상. 구분선을 border 토큰과 맞춘다.
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: tokens.colors.border },
            },
        },

        // TableHead 전역 배경. 별도 sx가 없으면 연한 회색 헤더를 쓴다.
        MuiTableHead: {
            styleOverrides: {
                root: { backgroundColor: tokens.colors.bg },
            },
        },
        // TableCell 전역 글자/구분선. head/body를 분리해서 정보 위계를 만든다.
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 700,
                    fontSize: 12,
                    color: tokens.colors.secondary,
                    borderBottom: `1px solid ${tokens.colors.border}`,
                },
                body: {
                    fontSize: 13,
                    borderBottom: `1px solid ${tokens.colors.borderLight}`,
                },
            },
        },
        // TableRow 전역 hover. 마지막 행 border 제거로 표 하단이 두꺼워 보이지 않게 한다.
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&:hover": { backgroundColor: tokens.colors.bg },
                    "&:last-child td": { borderBottom: "none" },
                },
            },
        },

        // Tooltip 전역 스타일. 어두운 배경으로 작은 설명을 띄운다.
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: tokens.colors.tooltip,
                    fontSize: 11,
                    borderRadius: 4,
                },
                arrow: { color: tokens.colors.tooltip },
            },
        },

        // Avatar 전역 스타일. 사람/초기값 표시 시 primary 계열로 통일한다.
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.colors.primaryLight,
                    color: tokens.colors.primaryDark,
                    fontWeight: 700,
                },
            },
        },

        // Select 전역 기본값 + radius(TextField outlined와 같은 곡률).
        // displayEmpty: 값이 "" 면 MUI 는 isFilled("")===false 라 표시값을 아예 계산하지 않는다 →
        // <MenuItem value="">전체</MenuItem> 이 빈칸으로 보인다. 사용처마다 붙이던 걸 여기로 올려 누락을 막는다.
        MuiSelect: {
            defaultProps: { displayEmpty: true },
            styleOverrides: {
                root: { borderRadius: 6 },
            },
        },

        // Select 드롭다운 팝업 종이 — 공통 dropdownPaper 사용.
        MuiMenu: {
            styleOverrides: {
                list: { paddingTop: 4, paddingBottom: 4 },
                paper: dropdownPaper,
            },
        },
        // Select 드롭다운 항목 — 공통 dropdownItem/Selected 사용(Autocomplete 옵션과 동일).
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ...dropdownItem,
                    "&.Mui-selected": { ...dropdownItemSelected },
                    "&.Mui-selected:hover": { backgroundColor: tokens.colors.primaryLight },
                },
            },
        },

        // Autocomplete/ComboBox 팝업 — Select 메뉴와 100% 동일하게 보이도록 같은 스펙을 적용.
        // 시스템의 모든 자동완성(ComboBox·AppAutocomplete·거래처/직원/메일 등)이 이 스타일을 공유한다.
        MuiAutocomplete: {
            styleOverrides: {
                // 팝업은 트리거(입력창) 너비에 정확히 맞춘다.
                // MUI 는 popper 너비를 입력창 너비로 고정하므로, 종이를 width:100% 로 채워
                // 폼필드보다 넓어지지 않게 하고(벗어남 방지), 긴 항목은 dropdownItem 의 줄바꿈으로 처리한다.
                paper: { ...dropdownPaper, width: "100%", boxSizing: "border-box" },
                listbox: { padding: 4, maxHeight: 320 },
                option: {
                    ...dropdownItem,
                    "&.Mui-focused": { backgroundColor: tokens.colors.primaryBg },
                    '&[aria-selected="true"]': { ...dropdownItemSelected },
                    '&[aria-selected="true"].Mui-focused': { backgroundColor: tokens.colors.primaryLight },
                },
            },
        },

        // Alert 전역 스타일. 경고/성공 안내 박스의 radius와 글자 굵기 기준이다.
        MuiAlert: {
            styleOverrides: {
                root: { borderRadius: 8, fontWeight: 500 },
            },
        },
    },
    });
};
